import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { AGENT_URL } from '../config/env'
import type { FeatureFlags as OrgFeatureFlags } from '@/types/configuration'

// Types matching backend models
export interface SuggestedPrompt {
  title: string
  message: string
}

/** How a rubric is graded; see `hoku.configuration.RubricType`. */
export type RubricType =
  | 'pass_fail'
  | 'classification'
  | 'multi_label'
  | 'rating'

/** A classification/multi-label label, or a rating level (lowest first). */
export interface RubricOption {
  label: string
  description: string
}

/**
 * Whether relay grades a rubric. Off rubrics stay listed (and filterable);
 * archived ones are hidden as if deleted. Missing: on.
 */
export type RubricState = 'on' | 'off' | 'archived'

export interface CallAnalysisRubric {
  /** Assigned by the server on save; never generated here. */
  id?: string | null
  title: string
  prompt: string
  /** Missing on rubrics saved before rubrics had types: pass/fail. */
  type?: RubricType
  options?: RubricOption[]
  /** Show the judge the system prompt the agent ran with. Missing: off. */
  include_agent_prompt?: boolean
  state?: RubricState
  /**
   * Careforce's rubrics only: `shared` lists it, locked, on every customer
   * org's page. Missing: internal. Either way it grades every org's calls.
   */
  visibility?: 'shared' | 'internal'
}

export interface AgentConfig {
  enabled: boolean
  read_allowed_tables: string[]
  write_allowed_tables: string[]
  enabled_tools: string[]
  suggested_prompts: SuggestedPrompt[]
}

export interface CareforceConfig {
  david: AgentConfig
  hanna: AgentConfig
}

export type FeatureFlags = OrgFeatureFlags

export interface CallingConfig {
  /** Org default holiday dates ('YYYY-MM-DD') when outbound calling is blocked. */
  holidays: string[]
}

export interface OrgConfig {
  agents: CareforceConfig
  features: FeatureFlags
  org_instructions: string
  call_analysis_rubrics: CallAnalysisRubric[]
  calling?: CallingConfig
}

export interface OrgConfigRow {
  org_slug: string
  config: OrgConfig
  updated_at: string
  created_at: string
}

export interface AgentTool {
  name: string
  allowed_roles: string[]
}

export interface AgentToolRegistry {
  david: AgentTool[]
  hanna: AgentTool[]
}

// API functions
async function fetchAllConfigs(token: string): Promise<OrgConfigRow[]> {
  const response = await fetch(`${AGENT_URL}/admin/config`, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(
      error.error || `Failed to fetch configs: ${response.status}`
    )
  }

  const data = await response.json()
  return data.configs
}

async function fetchAgentTools(token: string): Promise<AgentToolRegistry> {
  const response = await fetch(`${AGENT_URL}/admin/config/agent-tools`, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(
      error.error || `Failed to fetch agent tools: ${response.status}`
    )
  }

  const data = await response.json()
  return data.tools
}

async function fetchConfig(
  token: string,
  orgSlug: string
): Promise<{ org_slug: string; config: OrgConfig }> {
  const response = await fetch(`${AGENT_URL}/admin/config/${orgSlug}`, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || `Failed to fetch config: ${response.status}`)
  }

  return response.json()
}

async function updateConfig(
  token: string,
  orgSlug: string,
  config: OrgConfig
): Promise<{ success: boolean; org_slug: string; updated_at: string }> {
  const response = await fetch(`${AGENT_URL}/admin/config/${orgSlug}`, {
    method: 'POST',
    headers: {
      Authorization: token,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ config }),
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(
      error.error || `Failed to update config: ${response.status}`
    )
  }

  return response.json()
}

/**
 * Hook to list all organization configurations (super user only).
 * @param enabled - Whether to enable the query. Defaults to true.
 */
export function useListConfigs(enabled: boolean = true) {
  const { getToken } = useAuth()

  return useQuery({
    queryKey: ['admin-configs'],
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchAllConfigs(token)
    },
    staleTime: 30000, // 30 seconds
    enabled,
  })
}

export function useAgentTools(enabled: boolean = true) {
  const { getToken } = useAuth()

  return useQuery({
    queryKey: ['admin-agent-tools'],
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return fetchAgentTools(token)
    },
    staleTime: 300000,
    enabled,
  })
}

/**
 * Hook to get a specific organization configuration. Super users may read any
 * org; other global admins only their own.
 */
export function useGetConfig(orgSlug: string | null) {
  const { getToken } = useAuth()

  return useQuery({
    queryKey: ['admin-config', orgSlug],
    queryFn: async () => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      if (!orgSlug) {
        throw new Error('No org slug provided')
      }
      return fetchConfig(token, orgSlug)
    },
    enabled: !!orgSlug,
  })
}

/**
 * Hook to update an organization configuration. Super users may write any org;
 * other global admins only their own.
 */
export function useUpdateConfig() {
  const { getToken } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      orgSlug,
      config,
    }: {
      orgSlug: string
      config: OrgConfig
    }) => {
      const token = await getToken()
      if (!token) {
        throw new Error('No authentication token available')
      }
      return updateConfig(token, orgSlug, config)
    },
    onSuccess: (_, variables) => {
      // Invalidate queries to refresh the data
      queryClient.invalidateQueries({ queryKey: ['admin-configs'] })
      queryClient.invalidateQueries({
        queryKey: ['admin-config', variables.orgSlug],
      })
      // Public /configuration (e.g. Call Logs feature gates) caches per org
      queryClient.invalidateQueries({ queryKey: ['org-config'] })
    },
  })
}
